import React, { useEffect, useState, useCallback } from 'react';
import { apiFetch } from '../utils/serverConfig';

export default function StudentCollectables({ wallet, student = null, orderHistory = [] }) {
  const [minted, setMinted] = useState([]);
  const [unminted, setUnminted] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    try {
      // Priority: if orderHistory provided, fetch all orders and filter by IDs to build mint lists
      setLoading(true);
      setError(null);
      if (Array.isArray(orderHistory) && orderHistory.length > 0) {
        const allRes = await apiFetch('/api/orders');
        if (!allRes.ok) throw new Error('Failed to fetch orders');
        const all = await allRes.json();
        const arr = Array.isArray(all.orders) ? all.orders : (Array.isArray(all) ? all : []);
        const matched = arr.filter(o => orderHistory.includes(o.id));
        const m = matched.filter(o => !!(o.nft && (o.nft.status === 'minted' || (o.meta && o.meta.nftMinted))));
        const u = matched.filter(o => !(o.nft && o.nft.status === 'minted') && !(o.meta && o.meta.nftMinted));
        setMinted(m);
        setUnminted(u);
        return;
      }

      let walletToUse = wallet;
      if (!walletToUse) {
        try {
          if (window.ethereum && window.ethereum.request) {
            const accs = await window.ethereum.request({ method: 'eth_accounts' });
            if (accs && accs.length) walletToUse = accs[0];
          }
        } catch (e) { /* ignore */ }
      }

      if (!walletToUse) {
        setMinted([]);
        setUnminted([]);
        return;
      }

      const w = encodeURIComponent(walletToUse);
      const [mRes, uRes] = await Promise.all([
        apiFetch(`/api/nfts/by-wallet/${w}`),
        apiFetch(`/api/nfts/unminted/by-wallet/${w}`)
      ]);

      let m = [];
      let u = [];
      if (mRes && mRes.ok) m = await mRes.json().then(d => d.minted || d || []);
      if (uRes && uRes.ok) u = await uRes.json().then(d => d.unminted || d || []);

      const normalizeIpfsLink = (link) => {
        try {
          if (!link) return null;
          if (String(link).startsWith('ipfs://')) return `https://gateway.pinata.cloud/ipfs/${String(link).replace(/^ipfs:\/\//, '')}`;
          return link;
        } catch (e) { return null; }
      };

      const enrichWithImages = async (arr) => {
        return Promise.all((arr || []).map(async (o) => {
          try {
            let image = (o.nft && o.nft.image) || (o.meta && o.meta.image) || null;
            const uri = (o.nft && (o.nft.tokenURI || o.nft.ipfsHash)) || (o.meta && (o.meta.tokenURI || o.meta.ipfsHash)) || null;
            if (!image && uri) {
              if (String(uri).startsWith('ipfs://')) {
                image = normalizeIpfsLink(uri);
              } else if (String(uri).endsWith('.json') || String(uri).toLowerCase().includes('/metadata') || String(uri).toLowerCase().includes('tokenuri')) {
                try {
                  const fetched = await fetch(uri);
                  const json = await fetched.json().catch(() => null);
                  if (json && json.image) image = json.image.startsWith('ipfs://') ? normalizeIpfsLink(json.image) : json.image;
                } catch (e) { /* ignore */ }
              } else if (/^https?:\/\/.+\.(png|jpg|jpeg|gif|webp)/i.test(String(uri))) {
                image = uri;
              }
            }
            return { ...o, __image: image };
          } catch (e) {
            return { ...o, __image: null };
          }
        }));
      };

      const enrichedM = await enrichWithImages(Array.isArray(m) ? m : []);
      const enrichedU = await enrichWithImages(Array.isArray(u) ? u : []);

      setMinted(enrichedM);
      setUnminted(enrichedU);
    } catch (e) {
      console.warn('Failed to load collectables', e);
      setError(String(e && (e.message || e)));
    } finally {
      setLoading(false);
    }
  }, [wallet, orderHistory]);

  useEffect(() => {
    load();
    const onUpdate = () => load();
    window.addEventListener('orders-updated', onUpdate);
    window.addEventListener('store:orderCreated', onUpdate);
    window.addEventListener('student-updated', onUpdate);
    return () => {
      window.removeEventListener('orders-updated', onUpdate);
      window.removeEventListener('store:orderCreated', onUpdate);
      window.removeEventListener('student-updated', onUpdate);
    };
  }, [wallet, load]);

  // Resolve IPFS/tokenURIs into gateway HTTP URLs
  const resolveToGateway = (uri) => {
    try {
      if (!uri) return null;
      const s = String(uri);
      if (s.startsWith('ipfs://')) return `https://gateway.pinata.cloud/ipfs/${s.replace(/^ipfs:\/\//, '')}`;
      // common pattern: https://ipfs.io/ipfs/<CID>/... or /ipfs/<CID>
      const m = s.match(/(?:ipfs.io\/ipfs\/|\/ipfs\/)([A-Za-z0-9_-]{20,})/);
      if (m && m[1]) return `https://gateway.pinata.cloud/ipfs/${m[1]}`;
      // if it's a raw CID (46 chars base58) or shorter, attempt pinata
      if (/^[A-Za-z0-9_-]{20,}$/i.test(s) && s.length >= 20 && s.length <= 64) return `https://gateway.pinata.cloud/ipfs/${s}`;
      return uri;
    } catch (e) {
      return uri;
    }
  };

  const handleImgError = (e) => {
    try {
      const img = e && e.target;
      if (!img) return;
      if (img.dataset && img.dataset.fallbackTried) return;
      img.dataset.fallbackTried = '1';
      const orig = img.src || img.getAttribute('src') || '';
      // try to build a gateway URL
      const alt = resolveToGateway(orig);
      if (alt && alt !== orig) {
        img.src = alt;
        return;
      }
      // try alternate gateway
      if (orig && !orig.includes('ipfs.io')) img.src = orig.replace(/^(https?:\/\/)/, 'https://ipfs.io/ipfs/');
    } catch (err) {
      // swallow
    }
  };

  const mint = async (order) => {
    try {
      const item = (order.items && order.items[0]) || {};
      // try raffle mint first
      const body = { ticketId: item.id || item.name || order.id, to: wallet };
      const raffleResp = await apiFetch('/api/raffle/mint', { method: 'POST', body: JSON.stringify(body) });
      if (raffleResp && raffleResp.ok) {
        // success
        await raffleResp.json().catch(() => null);
        // refresh
        await load();
        window.dispatchEvent(new CustomEvent('orders-updated', { detail: { orderId: order.id } }));
        return true;
      }

      // fallback to order-specific mint endpoint
      const fallback = await apiFetch(`/api/orders/${encodeURIComponent(order.id)}/mint`, { method: 'POST', body: JSON.stringify({ itemId: item.id, toAddress: wallet }) });
      if (fallback && fallback.ok) {
        await fallback.json().catch(() => null);
        await load();
        window.dispatchEvent(new CustomEvent('orders-updated', { detail: { orderId: order.id } }));
        return true;
      }

      // As a last-resort for local testing environments, call the server-side simulated mint endpoint
      // which marks the order as minted without performing an on-chain transaction.
      const mark = await apiFetch(`/api/orders/${encodeURIComponent(order.id)}/mark-minted`, { method: 'POST', body: JSON.stringify({ to: wallet }) });
      if (mark && mark.ok) {
        await mark.json().catch(() => null);
        await load();
        window.dispatchEvent(new CustomEvent('orders-updated', { detail: { orderId: order.id } }));
        return true;
      }

      const rBody = await (raffleResp ? raffleResp.json().catch(()=>null) : null) || await (fallback ? fallback.json().catch(()=>null) : null) || {};
      throw new Error(rBody.error || 'Mint failed');
    } catch (e) {
      alert('Mint failed: ' + (e && (e.message || e)));
      console.warn('mint failed', e);
      return false;
    }
  };

  return (
    <div className="store-wrap" style={{ padding: 12 }}>
      <h3>Collectables</h3>
      {!wallet && <div className="empty">Connect your wallet to see your collectables.</div>}
      {loading && <div>Loading…</div>}
      {error && <div style={{ color: 'var(--danger)' }}>Error: {error}</div>}

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
        <div>
          <h4>Unminted</h4>
          {unminted.length === 0 && <div className="empty">No unminted collectables.</div>}
          <div className="store-grid">
            {unminted.map(o => (
              <div key={o.id} className="store-card" style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                  <div style={{ width: 84, height: 84, borderRadius: 12, overflow: 'hidden', position: 'relative', background: '#071022' }}>
                    {o.__image ? (
                      <img src={o.__image} alt="collectable" style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} onError={handleImgError} />
                    ) : (
                      <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#94a3b8', fontSize: 12 }}>No preview</div>
                    )}
                    <div style={{ position: 'absolute', left: 0, top: 0, width: '100%', height: '100%', pointerEvents: 'none', background: 'linear-gradient(180deg, rgba(255,255,255,0.06), rgba(255,255,255,0.02))' }} />
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 800 }}>{o.items && o.items[0] ? (o.items[0].name || o.items[0].id) : 'Collectable'}</div>
                    <div className="store-meta">Order: {o.id}</div>
                    <div style={{ fontSize: 12, color: 'var(--portal-muted)' }}>Pending mint — click Mint to create NFT</div>
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 8 }}>
                  <button className="btn-sm" onClick={() => mint(o)}>Mint NFT</button>
                  <button className="btn-sm" onClick={() => alert(JSON.stringify(o, null, 2))}>View</button>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div>
          <h4>Minted</h4>
          {minted.length === 0 && <div className="empty">No minted collectables.</div>}
          <div className="store-grid">
            {minted.map(o => (
              <div key={o.id} className="store-card" style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                  <div style={{ width: 84, height: 84, borderRadius: 12, overflow: 'hidden', position: 'relative', boxShadow: '0 6px 18px rgba(99,102,241,0.14)' }}>
                    {o.__image ? (
                      <img src={o.__image} alt="collectable" style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block', filter: 'saturate(1.05)' }} onError={handleImgError} />
                    ) : (
                      <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#94a3b8', fontSize: 12 }}>No preview</div>
                    )}
                    <div style={{ position: 'absolute', left: 0, top: 0, width: '100%', height: '100%', pointerEvents: 'none', background: 'linear-gradient(180deg, rgba(255,255,255,0.08), rgba(255,255,255,0.02))' }} />
                    <div style={{ position: 'absolute', inset: 0, boxShadow: '0 0 18px rgba(99,102,241,0.45)', borderRadius: 12, pointerEvents: 'none' }} />
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 800 }}>{o.items && o.items[0] ? (o.items[0].name || o.items[0].id) : 'Collectable'}</div>
                    <div className="store-meta">Order: {o.id}</div>
                    <div className="store-meta">Minted: {o.nft?.mintedAt || o.meta?.nftMintedAt || ''}</div>
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 8 }}>
                  {o.nft && o.nft.txHash && (
                    <a className="btn-sm" href={`https://etherscan.io/tx/${o.nft.txHash}`} target="_blank" rel="noreferrer">View Tx</a>
                  )}
                  <button className="btn-sm" onClick={() => alert(JSON.stringify(o, null, 2))}>View Metadata</button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
